import React, { FC, useRef, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Typography,
  Box,
  IconButton,
  InputAdornment,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ClearIcon from '@mui/icons-material/Clear';
import { BRAND, BRAND_DARK, BRAND_DARKER } from './healthWalletConstants';
import { useUploadUserDocumentMutation, useGetOnboardingStatusQuery, healthWalletApi } from './healthWalletSlice';
import { useAppDispatch } from '../../app/hooks';

// Mime types the AI extractor supports (must match HW SUPPORTED_MIME in src/user-data/workflow.ts).
const ACCEPT = 'application/pdf,image/jpeg,image/png,image/heic,image/heif,image/webp,text/plain';
const SUPPORTED = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
  'text/plain',
]);
// Keep under the 25 MB JSON body limit; base64 inflates ~33%, so cap the raw file at 18 MB.
const MAX_BYTES = 18 * 1024 * 1024;

interface UploadDocumentButtonProps {
  // Set when replacing an existing document slot (user-driven replace, same org_id).
  replaceOrgId?: string;
  // Compact icon-only label variant for inline row "Replace".
  label?: string;
  // Called after a successful upload so the parent can refetch the list.
  onUploaded?: () => void;
}

// Read a File as a bare base64 string (strips the "data:<mime>;base64," prefix).
function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('file read failed'));
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Mobile-friendly upload control. A contained button opens the OS file picker (plain `accept`,
 * no `capture`, so Camera / Photo Library / Files are all offered). After pick, a small dialog
 * captures an optional description, then uploads as base64 JSON. Lives in the My Documents
 * section header; its onClick stops propagation so tapping it never toggles the accordion.
 * See USER_DOCUMENTS_UI_PLAN.md §4.
 */
export const UploadDocumentButton: FC<UploadDocumentButtonProps> = ({ replaceOrgId, label = 'Upload', onUploaded }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [uploadUserDocument, { isLoading }] = useUploadUserDocumentMutation();
  const dispatch = useAppDispatch();

  // A health-data activity (IAS/Epic sync or another document extraction) holds the per-patient
  // lock and serializes all of them — so uploading is disabled while one is in flight, exactly
  // as the retrieve buttons are. The shared signal is dataRequestStatus (see USER_DATA_DESIGN.md).
  const { data: onboardingStatus } = useGetOnboardingStatusQuery();
  const isDataRequestPending =
    onboardingStatus?.dataRequestStatus === 'pending' || onboardingStatus?.dataRequestStatus === 'processing';

  const openPicker = (e: React.MouseEvent) => {
    e.stopPropagation();
    setError(null);
    inputRef.current?.click();
  };

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so re-picking the same file fires change again.
    e.target.value = '';
    if (!file) return;
    if (!SUPPORTED.has(file.type)) {
      setError(`Unsupported file type${file.type ? ` (${file.type})` : ''}. Allowed: PDF, image, or text.`);
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('File is too large. Please choose a file under 18 MB.');
      return;
    }
    setDescription(file.name.replace(/\.[^.]+$/, ''));
    setPendingFile(file);
  };

  const cancel = () => {
    setPendingFile(null);
    setDescription('');
  };

  const confirmUpload = async () => {
    if (!pendingFile) return;
    setError(null);
    try {
      const contentBase64 = await readAsBase64(pendingFile);
      const res = await uploadUserDocument({
        mimeType: pendingFile.type,
        contentBase64,
        description: description.trim(),
        replaceOrgId,
      }).unwrap();
      // .unwrap throws only on HTTP>=400; guard the 2xx-with-embedded-error envelope too.
      if ((res as any)?.error || (res as any)?.success === false) {
        throw new Error((res as any)?.message || 'Upload failed');
      }
      // NO optimistic dataRequestStatus patch. The extraction's 'user-document' ias_request is created
      // INSIDE the background drain (per document, after this response), so no run id exists to patch
      // atomically — and a status-only patch with the OLD dataRequestId forges the SSE snapshot merge's
      // race signature (spurious status refetch; for an untrusted browser in an identity-marker gap it
      // can even flip the screen to re-verify). The drain's first SSE progress event flips the real
      // status (~1-2s) and disables the retrieve buttons; the per-patient advisory lock already guards
      // double-runs server-side in the meantime. See project_hw_seed_optimistic_interaction.
      cancel();
      onUploaded?.();
    } catch (err: any) {
      setError(err?.data?.message || err?.message || 'Upload failed. Please try again.');
    }
  };

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={onFilePicked} />
      <Button
        variant="contained"
        size="small"
        startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
        onClick={openPicker}
        disabled={isLoading || isDataRequestPending}
        sx={{
          bgcolor: BRAND_DARKER,
          borderRadius: 50,
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': { bgcolor: '#5a3fc0' },
        }}
      >
        {label}
      </Button>

      {error && !pendingFile && (
        <Alert severity="error" sx={{ mt: 1, borderRadius: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Dialog
        open={!!pendingFile}
        onClose={cancel}
        fullWidth
        maxWidth="xs"
        slotProps={{
          paper: {
            sx: {
              borderRadius: 4,
              p: 0.5,
              background: `linear-gradient(135deg, ${BRAND} 0%, #d7d3f0 50%, ${BRAND_DARK} 100%)`,
            },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#1c1c1e' }}>{replaceOrgId ? 'Replace document' : 'Add a document description'}</DialogTitle>
        <DialogContent>
          {pendingFile && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, wordBreak: 'break-word' }}>
              {pendingFile.name} · {(pendingFile.size / 1024).toFixed(0)} KB
            </Typography>
          )}
          {/* Static label above the field (not MUI's floating label) — avoids the notch's white
              background poking over the purple gradient at the field's top edge. */}
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 0.5, mb: 0.25, ml: 0.5, fontWeight: 400, color: 'rgba(0,0,0,0.6)' }}
          >
            Description
          </Typography>
          <TextField
            autoFocus
            fullWidth
            placeholder="e.g. Lab results from June visit"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isLoading}
            slotProps={{
              input: {
                endAdornment: description ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear description"
                      onClick={() => setDescription('')}
                      disabled={isLoading}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              },
            }}
            sx={{
              // Solid white, flat field — no elevation; keep the border neutral grey even on focus.
              '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff', boxShadow: 'none' },
              // No floating label → collapse the notch legend so the top border is one continuous line.
              '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.23)', borderWidth: 1, top: 0 },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.23)' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.23)', borderWidth: 1 },
            }}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 3 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={cancel} disabled={isLoading} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={confirmUpload}
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ bgcolor: BRAND_DARKER, textTransform: 'none', fontWeight: 600, borderRadius: 50, '&:hover': { bgcolor: '#5a3fc0' } }}
          >
            {replaceOrgId ? 'Replace' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
